import { Footer } from '@jusbrasil/footer'
import { Topbar, withTopbarProps } from '@jusbrasil/topbar'
import { withApollo } from '@jusbrasil-web/core-apollo'
import {
  FilterInput,
  HaystackItem,
  HaystackResponse,
  JurisprudenceMetadata,
} from '@jusbrasil-web/shared-apollo-client/types'
import { useGTM } from '@jusbrasil-web/shared-gtm'
import { withUserSession } from '@jusbrasil-web/shared-user-session'
import classNames from 'classnames'
import { GetServerSideProps, InferGetServerSidePropsType } from 'next'
import { useEffect } from 'react'

import {
  ClusterDocResults,
  CommunitySpecInvite,
  FollowUpQuestions,
  JusIAInsights,
  LegalThesisHeader,
  ThesisHead,
} from '../../components'
import { DEFAULT_DOCUMENT_LIMIT } from '../../constants'
import {
  ThesisClusterRootQuery,
  ThesisClusterRootQueryVariables,
} from './__generated__/query.graphql'
import { getClusterQueryFilter } from './cluster-filter'
import { ThesisClusterRoot } from './query'
import styles from './thesis-cluster.module.scss'

export interface ThesisClusterPageProps {
  clusterLabel: string
  data: ThesisClusterRootQuery
  clusterId: string
  canonicalUrl: string
}

export type ThesisClusterPageParams = {
  id: string
}

export function ThesisClusterPage(props: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const { clusterLabel, data, clusterId, canonicalUrl } = props

  const { items, hits } = data.root.searchHaystack as HaystackResponse

  const gtm = useGTM()

  useEffect(() => {
    gtm.push({
      ga4_resource_id: clusterId,
      ga4_content_subgroup: 'teses',
    })
  }, [gtm, clusterId])

  return (
    <div>
      <ThesisHead
        clusterLabel={clusterLabel}
        items={items?.map((item: HaystackItem) => item.data as JurisprudenceMetadata)}
        canonicalUrl={canonicalUrl}
      />
      <Topbar variant="slim" />
      <main>
        <LegalThesisHeader title={clusterLabel} clusterId={clusterId} />
        <div className={classNames(styles.container, styles.resultsContainer)}>
          <JusIAInsights totalDocs={hits} />
          <ClusterDocResults
            documents={items as HaystackItem[]}
            clusterId={clusterId}
            hits={hits}
          />
        </div>
      </main>
      <div className={classNames(styles.container, styles.footnoteContainer)}>
        <FollowUpQuestions clusterId={clusterId} clusterLabel={clusterLabel} />
        <CommunitySpecInvite clusterId={clusterId} />
      </div>
      <Footer />
    </div>
  )
}

const getServerSideProps: GetServerSideProps<
  ThesisClusterPageProps,
  ThesisClusterPageParams
> = async (ctx) => {
  const { id: clusterId } = ctx.params ?? {}
  if (!clusterId) {
    return {
      notFound: true,
    }
  }

  const filters: FilterInput[] = getClusterQueryFilter(clusterId)

  const variables: ThesisClusterRootQueryVariables = {
    filters,
    limit: DEFAULT_DOCUMENT_LIMIT,
  }
  const { client, withApolloProps } = withApollo(ctx)
  const { data } = await client.query<ThesisClusterRootQuery>({
    query: ThesisClusterRoot,
    variables,
  })
  if (data?.root?.searchHaystack?.hits === 0 || data?.root?.searchHaystack?.items?.length === 0) {
    return {
      notFound: true,
    }
  }
  const clusterLabel = getClusterLabel(data, clusterId)

  if (!clusterLabel) {
    return {
      notFound: true,
    }
  }

  const canonicalUrl = `https://www.jusbrasil.com.br/teses-juridicas/${clusterId}`
  return {
    props: {
      ...withApolloProps,
      ...withTopbarProps(ctx),
      ...withUserSession(ctx),
      data,
      clusterLabel,
      clusterId,
      canonicalUrl,
    },
  }
}

function getClusterLabel(data: ThesisClusterRootQuery, clusterId: string) {
  const documentFromCluster = data?.root?.searchHaystack?.items?.[0]?.data as JurisprudenceMetadata
  return documentFromCluster?.addons?.legalThesisClusterLabels?.find(
    (label) => label.clusterId === clusterId
  )?.clusterLabel
}

export const ThesisClusterPageExports = { getServerSideProps }
